/**
 * Two-level header for the Supplier Performance table.
 *
 * Split out of PerformanceTable (which was past the 500-line limit). The
 * frozen-column `left` offsets come from `frozenColumns` so the header and
 * PerformanceTableRow cannot drift apart.
 *
 * Customer review 11 Sep (item 4): no Supplier Code column at the top
 * level. A supplier is known by a different code at each customer site (one supplier
 * ALLOYS UK has six), so the row that stands for the supplier as a whole has no
 * single code; each site's code is shown on its breakdown row, and on the
 * Customer cell when the row covers exactly one site.
 */

interface PerformanceTableHeaderProps {
  viewerIsSupplier: boolean;
  showSupplierSite: boolean;
  qualityLabel: string;
}

/** Widths of the frozen leading columns, in px. */
const SUPPLIER_W = 240;
const SITE_W = 200;
const CUSTOMER_W = 200;

/**
 * `left` offsets of the frozen columns for the current layout.
 *
 * @param viewerIsSupplier - supplier-side page (no Supplier column)
 * @param showSupplierSite - supplier-side GROUP view (Supplier Site column)
 * @returns the offsets and the total number of columns the table renders
 */
export function frozenColumns(viewerIsSupplier: boolean, showSupplierSite: boolean) {
  const lead = viewerIsSupplier ? (showSupplierSite ? SITE_W : 0) : SUPPLIER_W;
  return {
    customerLeft: lead,
    ratingLeft: lead + CUSTOMER_W,
    /** Every <td> in a row: leads + Customer + Rating + Warehouse + 8/7 quality + 5 cost + 5 PO + 4 GRN + CO2. */
    columnCount: (viewerIsSupplier ? (showSupplierSite ? 1 : 0) : 1) + 3 + (viewerIsSupplier ? 7 : 8) + 5 + 5 + 4 + 1,
  };
}

export function PerformanceTableHeader({
  viewerIsSupplier,
  showSupplierSite,
  qualityLabel,
}: PerformanceTableHeaderProps) {
  const { customerLeft, ratingLeft } = frozenColumns(viewerIsSupplier, showSupplierSite);
  return (
    // CR-4313: Sticky headers so column labels stay visible when scrolling
    <thead className="sticky top-0 z-20">
      {/* Top-level header groups */}
      {/* Solid bg (not /80 + backdrop-blur) so scrolled rows don't bleed through the sticky header. */}
      <tr className="border-b bg-muted">
        {!viewerIsSupplier && (
          <th
            className="p-3 text-left font-semibold sticky left-0 bg-muted z-10"
            style={{ minWidth: `${SUPPLIER_W}px` }}
            rowSpan={2}
          >
            Supplier
          </th>
        )}
        {showSupplierSite && (
          <th
            className="p-3 text-left font-semibold sticky left-0 bg-muted z-10"
            style={{ minWidth: `${SITE_W}px` }}
            rowSpan={2}
          >
            Supplier Site
          </th>
        )}
        <th
          className="p-3 text-center font-semibold sticky bg-muted z-10"
          style={{ minWidth: `${CUSTOMER_W}px`, left: `${customerLeft}px` }}
          rowSpan={2}
          title={viewerIsSupplier ? undefined : 'The customer sites this row covers — hover a cell for the list and each site’s supplier code'}
        >
          Customer
        </th>
        <th
          className="p-3 text-center font-semibold sticky bg-muted z-10 border-r"
          style={{ minWidth: '100px', left: `${ratingLeft}px` }}
          rowSpan={2}
        >
          Rating
        </th>
        <th className="p-3 text-center font-semibold" style={{ minWidth: '120px' }} rowSpan={2}>
          Warehouse
        </th>
        {/* SI-16: Response Score is pooled across customers by the serializer
            (no per-customer scope), so the supplier-side page hides it rather
            than show the same number on every row (R-133). CO2 is scoped per
            customer since the Customer review (msg 16) and shows on both. */}
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: viewerIsSupplier ? '700px' : '780px' }}
          colSpan={viewerIsSupplier ? 7 : 8}
        >
          Quality
        </th>
        {/* Cost: value, reject value and the three cost-of-quality figures
            (Customer review 11 Sep, msg 10 — legacy's formula, no click-through). */}
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: '580px' }}
          colSpan={5}
        >
          Cost
        </th>
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: '500px' }}
          colSpan={5}
        >
          Delivery (PO Line)
        </th>
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: '400px' }}
          colSpan={4}
        >
          Delivery (GRN)
        </th>
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: '150px' }}
          colSpan={1}
        >
          CO2 Emissions
        </th>
      </tr>
      {/* Second-level headers */}
      <tr className="border-b bg-muted">
        <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '100px' }}>
          Qty Received
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
          Qty Rejected
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '120px' }}>
          Customer Liability {qualityLabel}
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '120px' }}>
          Supplier Liability {qualityLabel}
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
          Total {qualityLabel}
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
          No. of NCRs
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
          Returns
        </th>
        {!viewerIsSupplier && (
          <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
            Response Score
          </th>
        )}
        <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '120px' }}>
          Total Value
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
          Reject Value
        </th>
        <th
          className="p-2 text-center text-xs font-medium"
          style={{ minWidth: '120px' }}
          title="Cost of Quality per NCR (Admin Setup → Quality) × Customer Liability NCRs + their reject value"
        >
          Customer Cost of Quality
        </th>
        <th
          className="p-2 text-center text-xs font-medium"
          style={{ minWidth: '120px' }}
          title="Cost of Quality per NCR (Admin Setup → Quality) × Supplier Liability (or undetermined) NCRs + their reject value"
        >
          Supplier Cost of Quality
        </th>
        <th
          className="p-2 text-center text-xs font-medium"
          style={{ minWidth: '120px' }}
          title="Customer + Supplier Cost of Quality"
        >
          Total Cost of Quality
        </th>
        <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '80px' }}>
          PO Lines
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
          OTIF
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
          Arrears
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '100px' }}>
          OTIF Score
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
          Qty Diff
        </th>
        <th className="p-2 text-center text-xs font-medium border-l" style={{ minWidth: '80px' }}>
          No. of GRNs
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
          Ontime
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
          Late
        </th>
        <th className="p-2 text-center text-xs font-medium" style={{ minWidth: '80px' }}>
          Early
        </th>
        <th
          className="p-2 text-center text-xs font-medium border-l"
          style={{ minWidth: '150px' }}
          title="Sum of the CO2 captured on the supplier's ASNs in this period"
        >
          Total CO2 Emissions
        </th>
      </tr>
    </thead>
  );
}
